import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, CheckCircle, Eye, EyeOff, Edit2, ArrowLeft } from "react-feather";
import { forgotPasswordTitle } from "../../Constant/title";
import { ConfirmPassword, Email, Password, RememberPassword } from "../../Constant";
import BaseButton from "../../BaseComponents/BaseButton";
import PublicPageLayout from "../../BaseComponents/PublicPageLayout";
import { forgotPasswordApi, verifyOtpApi } from "../../Api/AuthApi";
import { checkStatusCodeSuccess, errHandler } from "../../Constant/common";
import { emailRegex, passwordRegex, validationMessages } from "../../Constant/validation";
import { toast } from "react-toastify";

/* ── Password strength ─────────────────────────────────── */
const getStrength = (pw = "") => {
  if (!pw) return { score: 0, label: "", color: "transparent", width: "0%" };
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[!@#$%^&*()_\-+={}[\]:;,.?/\\|~]/.test(pw)) score++;
  const map = [
    { label: "Too short", color: "#f43f5e", width: "15%" },
    { label: "Weak", color: "#f43f5e", width: "30%" },
    { label: "Fair", color: "#f59e0b", width: "55%" },
    { label: "Good", color: "#06b6d4", width: "75%" },
    { label: "Strong", color: "#10b981", width: "100%" },
  ];
  return { score, ...map[score] };
};

const StrengthBar = ({ password }) => {
  const s = getStrength(password);
  if (!password) return null;
  return (
    <div style={{ marginTop: -8, marginBottom: 14 }}>
      <div
        style={{
          height: 4,
          borderRadius: 99,
          background: "var(--glass-border)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            borderRadius: 99,
            width: s.width,
            background: s.color,
            transition: "width 0.4s cubic-bezier(0.34,1.56,0.64,1), background 0.3s",
          }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
        <span style={{ fontSize: "0.6875rem", color: s.color, fontWeight: 600 }}>{s.label}</span>
        <div style={{ display: "flex", gap: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              style={{
                width: 18,
                height: 4,
                borderRadius: 99,
                background: s.score >= i ? s.color : "var(--glass-border)",
                transition: "background 0.3s",
              }}
            />
          ))}
        </div>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "3px 10px", marginTop: 6 }}>
        {[
          { label: "8+ chars", ok: password.length >= 8 },
          { label: "Uppercase", ok: /[A-Z]/.test(password) },
          { label: "Lowercase", ok: /[a-z]/.test(password) },
          { label: "Number", ok: /[0-9]/.test(password) },
          {
            label: "Special char",
            ok: /[!@#$%^&*()_\-+={}[\]:;,.?/\\|~]/.test(password),
          },
        ].map(({ label, ok }) => (
          <span
            key={label}
            style={{
              fontSize: "0.6875rem",
              fontWeight: 500,
              color: ok ? "#10b981" : "var(--text-muted)",
              display: "flex",
              alignItems: "center",
              gap: 3,
              transition: "color 0.2s",
            }}
          >
            <span>{ok ? "✓" : "○"}</span> {label}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ── 4-digit OTP input ─────────────────────────────────── */
const OTP_LENGTH = 4;

const OtpInput = ({ value = "", onChange, error }) => {
  const inputs = useRef([]);
  const digits = (value + "    ").slice(0, OTP_LENGTH).split("");

  const handleKey = (e, idx) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const next = digits.map((d, i) => (i === idx ? " " : d));
      onChange(next.join("").trimEnd());
      if (idx > 0 && digits[idx] === " ") inputs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowLeft" && idx > 0) inputs.current[idx - 1]?.focus();
    else if (e.key === "ArrowRight" && idx < OTP_LENGTH - 1) inputs.current[idx + 1]?.focus();
  };

  const handleChange = (e, idx) => {
    const raw = e.target.value.replace(/\D/g, "").slice(-1);
    const next = digits.map((d, i) => (i === idx ? raw || " " : d));
    onChange(next.join("").trimEnd());
    if (raw && idx < OTP_LENGTH - 1) inputs.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    onChange(pasted);
    inputs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <label
        style={{
          fontSize: "0.8125rem",
          fontWeight: 500,
          color: "var(--text-secondary)",
          display: "block",
          marginBottom: 10,
        }}
      >
        Verification Code
      </label>
      <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
        {digits.map((d, i) => {
          const filled = d.trim() !== "";
          return (
            <input
              key={i}
              ref={(el) => {
                inputs.current[i] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={filled ? d : ""}
              onChange={(e) => handleChange(e, i)}
              onKeyDown={(e) => handleKey(e, i)}
              onPaste={handlePaste}
              onFocus={(e) => e.target.select()}
              aria-label={`OTP digit ${i + 1}`}
              style={{
                width: 64,
                height: 68,
                textAlign: "center",
                fontSize: "1.5rem",
                fontWeight: 700,
                background: "var(--input-bg)",
                border: `2px solid ${error ? "var(--danger)" : filled ? "var(--brand-indigo)" : "var(--input-border)"}`,
                borderRadius: 16,
                color: "var(--text-primary)",
                outline: "none",
                transition: "border-color 0.2s, box-shadow 0.2s, transform 0.15s",
                boxShadow: filled ? "0 0 0 3px rgba(99,102,241,0.15)" : "none",
                transform: filled ? "scale(1.06)" : "scale(1)",
                caretColor: "var(--brand-indigo)",
              }}
            />
          );
        })}
      </div>
      {error && (
        <p
          style={{
            color: "var(--danger)",
            fontSize: "0.75rem",
            textAlign: "center",
            marginTop: 8,
            fontWeight: 500,
            margin: "8px 0 0",
          }}
        >
          {error}
        </p>
      )}
    </div>
  );
};

/* ── Step indicator ────────────────────────────────────── */
const Steps = ({ current }) => {
  const steps = ["Email", "Reset"];
  return (
    <div
      style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 24 }}
    >
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <React.Fragment key={i}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  background: done
                    ? "linear-gradient(135deg,#10b981,#059669)"
                    : active
                      ? "linear-gradient(135deg,#6366f1,#8b5cf6)"
                      : "var(--icon-btn-bg)",
                  color: done || active ? "#fff" : "var(--text-muted)",
                  border: done || active ? "none" : "1px solid var(--glass-border)",
                  boxShadow: active ? "0 4px 14px rgba(99,102,241,0.4)" : "none",
                  transition: "all 0.3s",
                }}
              >
                {done ? <CheckCircle size={14} strokeWidth={2.5} /> : i + 1}
              </div>
              <span
                style={{
                  fontSize: "0.625rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: active ? "var(--brand-indigo)" : done ? "#10b981" : "var(--text-muted)",
                  transition: "color 0.3s",
                }}
              >
                {label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                style={{
                  flex: 1,
                  height: 2,
                  margin: "0 8px",
                  marginBottom: 18,
                  background: done
                    ? "linear-gradient(90deg,#10b981,#059669)"
                    : "var(--glass-border)",
                  borderRadius: 99,
                  transition: "background 0.4s",
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ── Password field ─────────────────────────────────────── */
const PasswordField = ({ label, name, value, onChange, onBlur, error, touched, placeholder }) => {
  const [show, setShow] = useState(false);
  const isInvalid = !!(touched && error);
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        htmlFor={name}
        style={{
          fontSize: "0.8125rem",
          fontWeight: 500,
          color: "var(--text-secondary)",
          display: "block",
          marginBottom: 5,
        }}
      >
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          id={name}
          name={name}
          type={show ? "text" : "password"}
          value={value || ""}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          style={{
            width: "100%",
            padding: "9px 40px 9px 14px",
            background: "var(--input-bg)",
            border: `1px solid ${isInvalid ? "var(--danger)" : "var(--input-border)"}`,
            borderRadius: 12,
            color: "var(--text-primary)",
            fontSize: "0.8125rem",
            outline: "none",
            transition: "border-color 0.2s, box-shadow 0.2s",
            boxShadow: isInvalid ? "0 0 0 3px rgba(244,63,94,0.1)" : "none",
          }}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "var(--text-muted)",
            padding: 2,
            display: "flex",
            alignItems: "center",
            transition: "color 0.15s",
          }}
        >
          {show ? <EyeOff size={15} /> : <Eye size={15} />}
        </button>
      </div>
      {isInvalid && (
        <p style={{ color: "var(--danger)", fontSize: "0.75rem", marginTop: 4, fontWeight: 500 }}>
          {error}
        </p>
      )}
    </div>
  );
};

/* ── Email field ─────────────────────────────────────────── */
const EmailField = ({ value, onChange, onBlur, error, touched, disabled }) => {
  const isInvalid = !!(touched && error);
  return (
    <div style={{ marginBottom: 16 }}>
      <label
        htmlFor="fp-email"
        style={{
          fontSize: "0.8125rem",
          fontWeight: 500,
          color: "var(--text-secondary)",
          display: "block",
          marginBottom: 5,
        }}
      >
        {Email}
      </label>
      <input
        id="fp-email"
        name="email"
        type="email"
        value={value || ""}
        onChange={onChange}
        onBlur={onBlur}
        placeholder="you@example.com"
        disabled={disabled}
        style={{
          width: "100%",
          padding: "9px 14px",
          background: disabled ? "rgba(99,102,241,0.05)" : "var(--input-bg)",
          border: `1px solid ${isInvalid ? "var(--danger)" : disabled ? "rgba(99,102,241,0.2)" : "var(--input-border)"}`,
          borderRadius: 12,
          color: "var(--text-primary)",
          fontSize: "0.8125rem",
          outline: "none",
          transition: "border-color 0.2s, box-shadow 0.2s",
          opacity: disabled ? 0.7 : 1,
          cursor: disabled ? "not-allowed" : "text",
          boxShadow: isInvalid ? "0 0 0 3px rgba(244,63,94,0.1)" : "none",
        }}
      />
      {isInvalid && (
        <p style={{ color: "var(--danger)", fontSize: "0.75rem", marginTop: 4, fontWeight: 500 }}>
          {error}
        </p>
      )}
    </div>
  );
};

/* ── Step icon map (react-feather) ──────────────────────── */
const STEP_CONFIG = [
  {
    Icon: Mail,
    iconBg: "linear-gradient(135deg,#6366f1,#8b5cf6)",
    iconShadow: "0 8px 24px rgba(99,102,241,0.4)",
    title: "Forgot Password?",
    subtitle: "Enter your email and we'll send a 4-digit verification code",
  },
  {
    Icon: Lock,
    iconBg: "linear-gradient(135deg,#10b981,#06b6d4)",
    iconShadow: "0 8px 24px rgba(16,185,129,0.4)",
    title: "Reset Password",
    subtitle: "", // filled dynamically
  },
];

/* ── DRY BackToLogin link component ───────────────────────── */
const BackToLogin = ({ style }) => (
  <div style={{ textAlign: "center", ...style }}>
    <Link
      to="/login"
      style={{
        fontSize: "0.8125rem",
        color: "var(--text-muted)",
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      <ArrowLeft size={13} /> {RememberPassword}
    </Link>
  </div>
);

/* ── Main component ─────────────────────────────────────── */
const ForgotPassword = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = forgotPasswordTitle;
  }, []);

  const [step, setStep] = useState(0);
  const [loader, setLoader] = useState(false);
  const [resendLoader, setResendLoader] = useState(false);
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpErr, setOtpErr] = useState("");
  const [password, setPassword] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [pwTouched, setPwTouched] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [cfErr, setCfErr] = useState("");
  const [cfTouched, setCfTouched] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const validateEmail = useCallback((v) => {
    if (!v) return validationMessages.required(Email);
    if (!emailRegex.test(v)) return validationMessages.format(Email);
    return "";
  }, []);

  const validatePassword = useCallback((v) => {
    if (!v) return validationMessages.required(Password);
    if (v.length < 8) return validationMessages.passwordLength(Password, 8);
    if (!passwordRegex.test(v)) return validationMessages.passwordComplexity(Password);
    return "";
  }, []);

  /* Step 0 — send OTP */
  const handleSendOtp = (e) => {
    e.preventDefault();
    setEmailTouched(true);
    const err = validateEmail(email);
    setEmailErr(err);
    if (err) return;
    setLoader(true);
    verifyOtpApi({ email })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setStep(1);
          setResendTimer(60);
          toast.success("OTP sent to your email");
        } else {
          toast.error(resp?.message || "Failed to send OTP");
        }
      })
      .catch(errHandler)
      .finally(() => setLoader(false));
  };

  /* Step 1 — submit OTP + new password together */
  const handleReset = (e) => {
    e.preventDefault();
    let hasErr = false;
    if (otp.trim().length < OTP_LENGTH) {
      setOtpErr("Please enter the complete 4-digit code");
      hasErr = true;
    } else setOtpErr("");
    setPwTouched(true);
    setCfTouched(true);
    const pErr = validatePassword(password);
    const cErr = !confirm
      ? validationMessages.required(ConfirmPassword)
      : confirm !== password
        ? validationMessages.passwordsMatch(ConfirmPassword)
        : "";
    setPwErr(pErr);
    setCfErr(cErr);
    if (hasErr || pErr || cErr) return;
    setLoader(true);
    forgotPasswordApi({ email, otp: otp.trim(), password, confirmPassword: confirm })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp?.message || "Password reset successfully");
          navigate("/login");
        } else {
          toast.error(resp?.message || "Reset failed. Check your OTP and try again.");
        }
      })
      .catch(errHandler)
      .finally(() => setLoader(false));
  };

  const handleResend = () => {
    if (resendTimer > 0) return;
    setResendLoader(true);
    verifyOtpApi({ email })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setOtp("");
          setOtpErr("");
          setResendTimer(60);
          toast.success("New OTP sent");
        } else {
          toast.error(resp?.message || "Failed to resend OTP");
        }
      })
      .catch(errHandler)
      .finally(() => setResendLoader(false));
  };

  const cfg = {
    ...STEP_CONFIG[step],
    subtitle:
      step === 1
        ? `Enter the 4-digit code sent to ${email} and set your new password`
        : STEP_CONFIG[step].subtitle,
  };
  const StepIcon = cfg.Icon;

  return (
    <PublicPageLayout>
      {/* Card */}
      <div
        className="animate-scale-in"
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 440,
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 24,
          padding: "32px 32px 28px",
          boxShadow: "0 32px 80px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
          backdropFilter: "blur(24px) saturate(180%)",
        }}
      >
        {/* Shimmer */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            left: "-60%",
            width: "40%",
            height: "100%",
            background: "linear-gradient(90deg,transparent,rgba(255,255,255,0.03),transparent)",
            animation: "beam 5s ease-in-out infinite",
            pointerEvents: "none",
            borderRadius: 24,
          }}
        />

        <Steps current={step} />

        {/* Icon + heading */}
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <div
            key={step}
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              margin: "0 auto 12px",
              background: cfg.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: cfg.iconShadow,
              animation: "scaleIn 0.35s cubic-bezier(0.34,1.56,0.64,1)",
            }}
          >
            <StepIcon size={24} color="#fff" strokeWidth={2} />
          </div>
          <h1 style={{ fontSize: "1.25rem", fontWeight: 800, marginBottom: 4 }}>
            <span className="gradient-text">{cfg.title}</span>
          </h1>
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: "0.8125rem",
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            {cfg.subtitle}
          </p>
        </div>

        {/* ── Step 0: Email ── */}
        {step === 0 && (
          <form onSubmit={handleSendOtp} noValidate>
            <EmailField
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailTouched) setEmailErr(validateEmail(e.target.value));
              }}
              onBlur={() => {
                setEmailTouched(true);
                setEmailErr(validateEmail(email));
              }}
              error={emailErr}
              touched={emailTouched}
              disabled={loader}
            />
            <BaseButton
              type="submit"
              color="primary"
              loader={loader}
              disabled={loader}
              style={{
                width: "100%",
                padding: "0 20px",
                fontSize: "0.9375rem",
                justifyContent: "center",
                height: "32px",
                marginBottom: 14,
              }}
            >
              Send Verification Code
            </BaseButton>
            {!loader && <BackToLogin />}
          </form>
        )}

        {/* ── Step 1: OTP + new password ── */}
        {step === 1 && (
          <form onSubmit={handleReset} noValidate>
            {/* Email chip */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "6px 12px",
                borderRadius: 99,
                background: "rgba(99,102,241,0.08)",
                border: "1px solid rgba(99,102,241,0.2)",
                width: "fit-content",
                margin: "0 auto 16px",
              }}
            >
              <Mail size={12} color="var(--brand-indigo)" />
              <span
                style={{ fontSize: "0.8125rem", fontWeight: 600, color: "var(--brand-indigo)" }}
              >
                {email}
              </span>
              <button
                type="button"
                onClick={() => setStep(0)}
                disabled={loader}
                style={{
                  background: "none",
                  border: "none",
                  cursor: loader ? "not-allowed" : "pointer",
                  color: "var(--text-muted)",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  opacity: loader ? 0.5 : 1,
                }}
                title="Change email"
              >
                <Edit2 size={11} />
              </button>
            </div>

            <OtpInput value={otp} onChange={setOtp} error={otpErr} />

            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
                Didn&apos;t receive it?{" "}
              </span>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendTimer > 0 || resendLoader}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: resendTimer > 0 || resendLoader ? "not-allowed" : "pointer",
                  fontSize: "0.8125rem",
                  fontWeight: 600,
                  color:
                    resendTimer > 0 || resendLoader ? "var(--text-muted)" : "var(--brand-indigo)",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {resendLoader && (
                  <span className="ds-spinner ds-spinner-sm" style={{ width: 12, height: 12 }} />
                )}
                {resendLoader
                  ? "Sending..."
                  : resendTimer > 0
                    ? `Resend in ${resendTimer}s`
                    : "Resend code"}
              </button>
            </div>

            <PasswordField
              label={Password}
              name="password"
              value={password}
              placeholder="New password"
              onChange={(e) => {
                setPassword(e.target.value);
                if (pwTouched) setPwErr(validatePassword(e.target.value));
              }}
              onBlur={() => {
                setPwTouched(true);
                setPwErr(validatePassword(password));
              }}
              error={pwErr}
              touched={pwTouched}
            />
            <StrengthBar password={password} />

            <PasswordField
              label={ConfirmPassword}
              name="confirm_password"
              value={confirm}
              placeholder="Confirm new password"
              onChange={(e) => {
                setConfirm(e.target.value);
                if (cfTouched)
                  setCfErr(
                    e.target.value !== password
                      ? validationMessages.passwordsMatch(ConfirmPassword)
                      : "",
                  );
              }}
              onBlur={() => {
                setCfTouched(true);
                setCfErr(
                  confirm !== password ? validationMessages.passwordsMatch(ConfirmPassword) : "",
                );
              }}
              error={cfErr}
              touched={cfTouched}
            />

            {confirm && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  marginTop: -6,
                  marginBottom: 14,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: confirm === password ? "#10b981" : "#f43f5e",
                }}
              >
                {confirm === password ? <CheckCircle size={13} /> : <span>✗</span>}
                <span>{confirm === password ? "Passwords match" : "Passwords don't match"}</span>
              </div>
            )}

            <BaseButton
              type="submit"
              color="primary"
              loader={loader}
              disabled={
                loader ||
                otp.trim().length < OTP_LENGTH ||
                !password ||
                !confirm ||
                password !== confirm
              }
              style={{
                width: "100%",
                padding: "0 20px",
                fontSize: "0.9375rem",
                justifyContent: "center",
                height: "32px",
              }}
            >
              Reset Password
            </BaseButton>
            {!loader && <BackToLogin style={{ marginTop: 14 }} />}
          </form>
        )}
      </div>
    </PublicPageLayout>
  );
};

export default ForgotPassword;
